import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { ArrowRight } from 'lucide-react';

export const DemoContext = createContext({ open: () => {} });
export const useDemo = () => useContext(DemoContext);

export const useInView = (threshold = 0.2) => {
  const ref = useRef(null);
  const [seen, setSeen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || seen) return undefined;
    const io = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setSeen(true); io.disconnect(); } }, { threshold });
    io.observe(el);
    return () => io.disconnect();
  }, [seen, threshold]);
  return [ref, seen];
};

export const Reveal = ({ children, delay = 0, className = '', as: Tag = 'div', ...rest }) => {
  const [ref, seen] = useInView(0.15);
  return (
    <Tag ref={ref} className={`dl-reveal ${seen ? 'is-in' : ''} ${className}`} style={{ transitionDelay: `${delay}ms` }} {...rest}>
      {children}
    </Tag>
  );
};

export const Counter = ({ value, testid }) => {
  const [ref, seen] = useInView(0.4);
  const [shown, setShown] = useState(0);
  useEffect(() => {
    if (!seen || !value) return undefined;
    let raf; const t0 = performance.now();
    const tick = (t) => {
      const k = Math.min(1, (t - t0) / 1400);
      setShown(Math.round(value * (1 - (1 - k) ** 3)));
      if (k < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [seen, value]);
  return <span ref={ref} data-testid={testid}>{shown.toLocaleString('en-IN')}</span>;
};

export const Laptop = ({ src, alt, className = '' }) => (
  <div className={`dl-laptop ${className}`}>
    <div className="rounded-t-[14px] border border-[#1e293b] bg-[#0f172a] p-[1.4%] pb-[1.6%]">
      <div className="mb-[1%] flex gap-1.5 px-1"><i className="h-1.5 w-1.5 rounded-full bg-[#475569]" /><i className="h-1.5 w-1.5 rounded-full bg-[#475569]" /><i className="h-1.5 w-1.5 rounded-full bg-[#475569]" /></div>
      <img src={src} alt={alt} loading="lazy" className="block w-full rounded-[4px]" />
    </div>
    <div className="mx-[-4%] h-3 rounded-b-[12px] bg-gradient-to-b from-[#cbd5e1] to-[#94a3b8]" />
  </div>
);

export const Phone = ({ src, alt, className = '' }) => (
  <div className={`dl-phone rounded-[28px] border-[6px] border-[#0f172a] bg-[#0f172a] shadow-2xl ${className}`}>
    <img src={src} alt={alt} loading="lazy" className="block h-full w-full rounded-[22px] object-cover object-top" />
  </div>
);

export const DemoButton = ({ children = 'Book a demo', className = '', testid = 'book-demo-btn', dark }) => {
  const { open } = useDemo();
  return (
    <button type="button" onClick={open} data-testid={testid}
      className={`dl-btn inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 text-[15px] font-semibold ${dark ? 'dl-btn-light' : 'dl-btn-ink'} ${className}`}>
      {children} <ArrowRight size={16} />
    </button>
  );
};

export const Eyebrow = ({ children, dark }) => (
  <p className={`text-xs font-bold uppercase tracking-[0.18em] ${dark ? 'text-[#5eead4]' : 'text-[#0f766e]'}`}>{children}</p>
);

export const H2 = ({ children, dark, className = '' }) => (
  <h2 className={`dl-display mt-3 text-3xl font-semibold leading-[1.08] tracking-tight sm:text-4xl lg:text-5xl ${dark ? 'text-[#f8fafc]' : 'text-[#0f172a]'} ${className}`}>{children}</h2>
);

export const useSeo = ({ title, description, image = '/driveline/scenes/service-floor.webp' }) => {
  useEffect(() => {
    document.title = title;
    const set = (attr, key, val) => {
      let el = document.head.querySelector(`meta[${attr}="${key}"]`);
      if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); }
      el.setAttribute('content', val);
    };
    set('name', 'description', description);
    set('property', 'og:title', title);
    set('property', 'og:description', description);
    set('property', 'og:image', `${window.location.origin}${image}`);
    set('name', 'twitter:card', 'summary_large_image');
  }, [title, description, image]);
};
